import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { File, Play } from 'lucide-react';
import { useLocation } from 'wouter';

import { apiGet } from '../../lib/api_v2';
import { buildAttachmentLightboxSlide } from '../../lib/lightboxSlides';
import { logError } from '../../lib/logger';
import { requestMessageJump } from '../../lib/pendingMessageJump';
import { cn } from '../../lib/utils';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../../contexts/KeyMaster';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import ImageAttachmentLightbox from '../messages/ImageAttachmentLightbox';
import ContextMenuPortal from '../portals/ContextMenuPortal';

const MEDIA_PER_PAGE = 60;
const ZERO_POSITION = { x: 0, y: 0 };

const TILE_CLASS = 'relative aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-neutral-800';

const MediaGalleryTile = React.memo(function MediaGalleryTile({ attachment, canOpenInLightbox, onOpen, onOpenMenu }) {
  const contentType = attachment.metadata?.content_type || '';
  const isVideo = contentType.startsWith('video/');
  const thumbnailUrl = isVideo
    ? attachment.variants?.large?.public_url || attachment.variants?.medium?.public_url || null
    : attachment.variants?.thumb?.public_url || attachment.variants?.medium?.public_url || attachment.public_url || null;

  const handleClick = useCallback(
    function handleClick(event) {
      onOpen(attachment, event);
    },
    [attachment, onOpen],
  );

  const handleContextMenu = useCallback(
    function handleContextMenu(event) {
      event.preventDefault();
      onOpenMenu(attachment, event);
    },
    [attachment, onOpenMenu],
  );

  if (canOpenInLightbox) {
    return (
      <button
        type="button"
        data-app-region="none"
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        className={cn(TILE_CLASS, 'cursor-pointer')}
      >
        <img src={thumbnailUrl} alt={attachment.metadata?.file_name || 'Image'} loading="lazy" className="size-full object-cover" />
      </button>
    );
  }

  // Videos and other files have no lightbox slide type; open the original in a new tab.
  return (
    <a
      href={attachment.public_url}
      target="_blank"
      rel="noopener noreferrer"
      data-app-region="none"
      onContextMenu={handleContextMenu}
      className={cn(TILE_CLASS, 'block')}
    >
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt={attachment.metadata?.file_name || 'File'} loading="lazy" className="size-full object-cover" />
      ) : (
        <span className={cn('flex size-full items-center justify-center', isVideo && 'bg-gray-900 dark:bg-neutral-950')}>
          {!isVideo && <File className="size-6 text-gray-400 dark:text-neutral-500" strokeWidth={1.5} absoluteStrokeWidth />}
        </span>
      )}
      {isVideo && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Play className="size-6 text-white drop-shadow" strokeWidth={1.5} absoluteStrokeWidth />
        </span>
      )}
    </a>
  );
});

// Nested "Media" section of the chat overview modal: a paginated grid of the media
// attachments sent in this workspace, newest first. Image/PDF tiles open the shared
// PhotoSwipe lightbox; video/file tiles link out to the original.
const MediaGalleryPanel = () => {
  const { workspaceId } = useRouteWorkspace();
  const [, navigate] = useLocation();
  const [mediaAttachments, setMediaAttachments] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const abortRef = useRef(null);

  // While the lightbox is open it owns the top of the KeyMaster stack, so Esc closes
  // only PhotoSwipe instead of also dismissing the settings modal underneath.
  useRegisterWithKeyMaster(lightbox ? KEYCHAIN.LIGHTBOX__MEDIA_GALLERY : null);

  const loadPage = useCallback(
    async function loadPage(nextPage) {
      if (!workspaceId) return;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);

      try {
        const searchParams = new URLSearchParams({
          workspace_id: workspaceId,
          page: String(nextPage),
          per_page: String(MEDIA_PER_PAGE),
        });
        const response = await apiGet(`/media_attachments?${searchParams.toString()}`, { signal: controller.signal });
        const data = await response.json();

        if (controller.signal.aborted) return;

        if (data?.ok) {
          const responseData = data.data || {};
          const nextAttachments = responseData.media_attachments || [];
          setMediaAttachments((previous) => (nextPage === 1 ? nextAttachments : [...previous, ...nextAttachments]));
          setPage(responseData.page || nextPage);
          setTotalPages(responseData.total_pages || 0);
        } else {
          logError('media-gallery', `Failed to load media: ${JSON.stringify(data?.errors)}`);
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        logError('media-gallery', 'Failed to load media', error);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [workspaceId],
  );

  useEffect(
    function loadFirstPageForWorkspace() {
      setMediaAttachments([]);
      setPage(1);
      setTotalPages(0);
      setLightbox(null);
      void loadPage(1);
      return () => {
        if (abortRef.current) abortRef.current.abort();
      };
    },
    [loadPage],
  );

  // Only image/PDF attachments participate in the lightbox; the map lets a tile find
  // its slide position within the filtered list.
  const { lightboxSlides, slideIndexByAttachmentId } = useMemo(
    function buildLightboxSlides() {
      const slides = [];
      const indexById = new Map();
      for (const attachment of mediaAttachments) {
        const slide = buildAttachmentLightboxSlide(attachment);
        if (!slide) continue;
        indexById.set(attachment.id, slides.length);
        slides.push(slide);
      }
      return { lightboxSlides: slides, slideIndexByAttachmentId: indexById };
    },
    [mediaAttachments],
  );

  const handleOpenTile = useCallback(
    function handleOpenTile(attachment, event) {
      const index = slideIndexByAttachmentId.get(attachment.id);
      if (index === undefined) return;

      const openerRect = event?.currentTarget instanceof Element ? event.currentTarget.getBoundingClientRect() : null;
      const thumbBounds =
        openerRect && Number.isFinite(openerRect.left) && Number.isFinite(openerRect.top) && Number.isFinite(openerRect.width)
          ? { x: openerRect.left, y: openerRect.top, w: openerRect.width }
          : null;
      const slides = lightboxSlides.map((slide, slideIndex) => (slideIndex === index ? { ...slide, thumbBounds } : slide));

      setLightbox({ slides, index, thumbBounds });
    },
    [lightboxSlides, slideIndexByAttachmentId],
  );

  const handleCloseLightbox = useCallback(function closeLightbox() {
    setLightbox(null);
  }, []);

  const handleOpenTileMenu = useCallback(function openTileMenu(attachment, event) {
    setContextMenu({ position: { x: event.clientX, y: event.clientY }, attachment });
  }, []);

  const handleCloseContextMenu = useCallback(function closeContextMenu() {
    setContextMenu(null);
  }, []);

  // Same recipe as opening a search result: buffer the jump intent, then navigate to
  // the clean channel URL — dropping the modal param closes the settings modal, and
  // the channel's MessageFeed drains the buffer to land centered on the message.
  const jumpToAttachmentMessage = useCallback(
    function jumpToAttachmentMessage(attachment) {
      if (!attachment?.workspace_id || !attachment?.channel_id || !attachment?.message_sort_order) return;

      requestMessageJump({ channelId: attachment.channel_id, sortOrder: attachment.message_sort_order });
      navigate(`~/spaces/${attachment.workspace_id}/chats/${attachment.channel_id}`);
    },
    [navigate],
  );

  const handleShowInChat = useCallback(
    function showInChat() {
      jumpToAttachmentMessage(contextMenu?.attachment);
    },
    [contextMenu, jumpToAttachmentMessage],
  );

  const handleShowInChatFromLightbox = useCallback(
    function showInChatFromLightbox(slideData) {
      const attachment = mediaAttachments.find((candidate) => candidate.id === slideData?.attachmentId);
      if (!attachment) return;

      setLightbox(null);
      jumpToAttachmentMessage(attachment);
    },
    [jumpToAttachmentMessage, mediaAttachments],
  );

  const handleLoadMore = useCallback(
    function loadMore() {
      void loadPage(page + 1);
    },
    [loadPage, page],
  );

  let content = null;
  if (isLoading && mediaAttachments.length === 0) {
    content = <div className="flex items-center justify-center py-8 text-cursed text-gray-500 dark:text-neutral-400">Loading…</div>;
  } else if (mediaAttachments.length === 0) {
    content = <div className="flex items-center justify-center py-8 text-cursed text-gray-500 dark:text-neutral-400">No media yet</div>;
  } else {
    content = (
      <div className="grid grid-cols-3 gap-1">
        {mediaAttachments.map((attachment) => (
          <MediaGalleryTile
            key={attachment.id}
            attachment={attachment}
            canOpenInLightbox={slideIndexByAttachmentId.has(attachment.id)}
            onOpen={handleOpenTile}
            onOpenMenu={handleOpenTileMenu}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 pt-10 pb-5">
      {content}
      {page < totalPages && (
        <button
          type="button"
          data-app-region="none"
          onClick={handleLoadMore}
          disabled={isLoading}
          className="h-8 cursor-pointer self-center rounded-full border-[0.5px] border-gray-300 px-3 font-sans text-xs font-medium text-gray-600 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          Load more
        </button>
      )}
      <ImageAttachmentLightbox
        open={Boolean(lightbox)}
        onClose={handleCloseLightbox}
        slides={lightbox?.slides || []}
        index={lightbox?.index || 0}
        thumbBounds={lightbox?.thumbBounds || null}
        onShowInChat={handleShowInChatFromLightbox}
      />
      <ContextMenuPortal
        isOpen={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        position={contextMenu?.position ?? ZERO_POSITION}
        message={null}
        onAddReaction={null}
        clickedAttachment={contextMenu?.attachment ?? null}
        onShowInChat={handleShowInChat}
        className="z-(--stack-messages-lightbox-context-menu)"
      />
    </div>
  );
};

export default MediaGalleryPanel;
